/*
  Farb-Helfer für den Schichtplan-Export (PDF, Excel, Vorschau).
*/

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

// PDF/Excel können keine CSS-Variablen lesen – die Werte spiegeln darum die
// DS-Tokens: neutral-700, neutral-200 und neutral-900.
export const EXPORT_FALLBACK_ACCENT = "#657089";
export const EXPORT_NEUTRAL_BODY = "#EEF1F6";
const EXPORT_TEXT_PRIMARY = "#1F2840";
// Gedämpfte Schrift für Ferien und Absenzen (Slate 500).
const EXPORT_TEXT_MUTED = "#64748B";

export function normalizeHexColor(value, fallback = "") {
  const color = String(value || "").trim();
  if (!HEX_COLOR_PATTERN.test(color)) return fallback;
  if (color.length === 4) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`.toUpperCase();
  }
  return color.toUpperCase();
}

export function hexToRgb(value) {
  const hex = normalizeHexColor(value, "#000000").slice(1);
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function toHexPart(value) {
  return Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0");
}

export function mixHexColors(baseColor, mixColor, mixRatio) {
  const base = hexToRgb(baseColor);
  const mix = hexToRgb(mixColor);
  const ratio = Math.max(0, Math.min(1, Number(mixRatio) || 0));

  return `#${toHexPart(base.r + (mix.r - base.r) * ratio)}${toHexPart(base.g + (mix.g - base.g) * ratio)}${toHexPart(
    base.b + (mix.b - base.b) * ratio
  )}`.toUpperCase();
}

export function getReadableTextColor(backgroundColor, darkColor = EXPORT_TEXT_PRIMARY, lightColor = "#FFFFFF") {
  const { r, g, b } = hexToRgb(backgroundColor);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? darkColor : lightColor;
}

export function buildEntryPalette(entryColor, { monochrome = false } = {}) {
  const accent = monochrome ? EXPORT_FALLBACK_ACCENT : normalizeHexColor(entryColor, EXPORT_FALLBACK_ACCENT);
  const body = monochrome ? EXPORT_NEUTRAL_BODY : mixHexColors(accent, "#FFFFFF", 0.82);

  return {
    accent,
    body,
    // --ds-palette-neutral-400 als Rahmen, sonst der abgedunkelte Eigenton.
    border: monochrome ? "#D5DDE8" : mixHexColors(accent, EXPORT_TEXT_PRIMARY, 0.32),
    text: monochrome ? EXPORT_TEXT_PRIMARY : mixHexColors(accent, EXPORT_TEXT_PRIMARY, 0.6),
  };
}

/*
  Ferien und Absenzen wie im Raster (.spv2-grid-span-entry.is-absence): ein
  heller Grund mit diagonalen Streifen im Eigenton. Für den Aushang bewusst
  blasser als dort und mit gedämpfter Schrift – der Eigenton deutet die Art
  nur an. Kräftig gefärbt konkurrierten "Ferien" und "Unfall" mit Schichten
  derselben Farbe, und die Schichten sind das, was auf dem Plan zählt.
*/
export function buildTimeOffPalette(entryColor, { monochrome = false } = {}) {
  const { accent } = buildEntryPalette(entryColor, { monochrome });

  return {
    accent,
    body: mixHexColors(accent, "#FFFFFF", 0.95),
    stripe: mixHexColors(accent, "#FFFFFF", 0.83),
    border: mixHexColors(accent, "#FFFFFF", 0.74),
    text: mixHexColors(accent, EXPORT_TEXT_MUTED, 0.55),
  };
}
